import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import type { StreamServer } from '../types';
import { loadServers } from '../storage/streamPlayerStorage';
import { normalizeServerUrl, serversDuplicate } from '../utils/serverFormUtils';
import { COUNTRY_SELECT_OPTIONS } from '../utils/countryLabels';
import { streamGhostOnDarkHover } from '../darkThemeButtonProps';

interface Props {
  server: StreamServer | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (server: StreamServer) => void;
}

export function EditServerModal({ server, isOpen, onClose, onSave }: Props) {
  const [name, setName] = useState('');
  const [srv, setSrv] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [restrictCountry, setRestrictCountry] = useState(false);
  const [connectionCountryCode, setConnectionCountryCode] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (server && isOpen) {
      setName(server.name);
      setSrv(server.server);
      setUsername(server.username);
      setPassword(server.password);
      setShowPassword(false);
      setRestrictCountry(Boolean(server.restrictConnectionToCountry));
      setConnectionCountryCode(server.connectionCountryCode ?? '');
      setErrors({});
    }
  }, [server, isOpen]);

  const validate = (): boolean => {
    if (!server) return false;
    const e: Record<string, string> = {};
    const kind = server.sourceKind ?? 'xtream';
    if (!name.trim()) e.name = 'Name is required.';
    if (!srv.trim()) {
      e.server = kind === 'm3u_url' ? 'Playlist URL is required.' : 'Server URL is required.';
    }
    if (kind === 'xtream') {
      if (!username.trim()) e.username = 'Username is required.';
      if (!password) e.password = 'Password is required.';
    }
    if (restrictCountry && !connectionCountryCode.trim()) {
      e.connectionCountryCode = 'Select a country when restriction is enabled.';
    }
    const norm = normalizeServerUrl(srv);
    const dupUser = kind === 'm3u_url' ? '' : username.trim();
    if (Object.keys(e).length === 0 && serversDuplicate(loadServers(), norm, dupUser, server.id)) {
      e.server =
        kind === 'm3u_url'
          ? 'A server with this playlist URL already exists.'
          : 'A server with this URL and username already exists.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = () => {
    if (!server || !validate()) return;
    setSubmitting(true);
    try {
      const norm = normalizeServerUrl(srv);
      const kind = server.sourceKind ?? 'xtream';
      const updated: StreamServer = {
        ...server,
        name: name.trim(),
        server: norm,
        username: kind === 'm3u_url' ? '' : username.trim(),
        password: kind === 'm3u_url' ? '' : password,
        restrictConnectionToCountry: restrictCountry,
        connectionCountryCode: restrictCountry ? connectionCountryCode.trim().toUpperCase() : undefined,
        updatedAt: new Date().toISOString(),
      };
      onSave(updated);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent bg="#121a22" borderWidth="1px" borderColor="whiteAlpha.200">
        <ModalHeader color="white" fontSize="md">
          Edit server
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody>
          <VStack spacing={3} align="stretch">
            <FormControl isInvalid={Boolean(errors.name)}>
              <FormLabel color="whiteAlpha.800" fontSize="sm">
                Name
              </FormLabel>
              <Input
                value={name}
                onChange={(ev) => setName(ev.target.value)}
                size="sm"
                bg="whiteAlpha.100"
                borderColor="whiteAlpha.200"
                color="white"
              />
              <FormErrorMessage>{errors.name}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={Boolean(errors.server)}>
              <FormLabel color="whiteAlpha.800" fontSize="sm">
                {(server?.sourceKind ?? 'xtream') === 'm3u_url' ? 'Playlist URL' : 'Server URL'}
              </FormLabel>
              <Input
                value={srv}
                onChange={(ev) => setSrv(ev.target.value)}
                size="sm"
                bg="whiteAlpha.100"
                borderColor="whiteAlpha.200"
                color="white"
              />
              <FormErrorMessage>{errors.server}</FormErrorMessage>
            </FormControl>
            {(server?.sourceKind ?? 'xtream') === 'xtream' && (
              <>
                <FormControl isInvalid={Boolean(errors.username)}>
                  <FormLabel color="whiteAlpha.800" fontSize="sm">
                    Username
                  </FormLabel>
                  <Input
                    value={username}
                    onChange={(ev) => setUsername(ev.target.value)}
                    size="sm"
                    bg="whiteAlpha.100"
                    borderColor="whiteAlpha.200"
                    color="white"
                  />
                  <FormErrorMessage>{errors.username}</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={Boolean(errors.password)}>
                  <FormLabel color="whiteAlpha.800" fontSize="sm">
                    Password
                  </FormLabel>
                  <InputGroup size="sm">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(ev) => setPassword(ev.target.value)}
                      pr="2.5rem"
                      bg="whiteAlpha.100"
                      borderColor="whiteAlpha.200"
                      color="white"
                    />
                    <InputRightElement w="2.5rem" h="32px">
                      <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        icon={
                          showPassword ? <MdVisibilityOff size={18} /> : <MdVisibility size={18} />
                        }
                        size="xs"
                        variant="ghost"
                        color="whiteAlpha.600"
                        {...streamGhostOnDarkHover}
                        onClick={() => setShowPassword((v) => !v)}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{errors.password}</FormErrorMessage>
                </FormControl>
              </>
            )}
            <FormControl>
              <Checkbox
                isChecked={restrictCountry}
                onChange={(e) => setRestrictCountry(e.target.checked)}
                colorScheme="teal"
                color="whiteAlpha.900"
              >
                Restrict connection to
              </Checkbox>
              <Text fontSize="xs" color="whiteAlpha.500" mt={1} pl={6}>
                If enabled, VoraKit compares a <strong>detected-from-public-IP</strong> country (plus edge hints) to
                your selection before connecting. VPNs, proxies, or conflicting signals lower confidence — we never
                claim a verified country. A mismatch or unknown result blocks the library load for this server.
              </Text>
            </FormControl>
            {restrictCountry && (
              <FormControl isInvalid={Boolean(errors.connectionCountryCode)}>
                <FormLabel color="whiteAlpha.800" fontSize="sm">
                  Country
                </FormLabel>
                <Select
                  size="sm"
                  value={connectionCountryCode}
                  onChange={(e) => setConnectionCountryCode(e.target.value)}
                  placeholder="Select country"
                  bg="whiteAlpha.100"
                  borderColor="whiteAlpha.200"
                  color="white"
                  iconColor="whiteAlpha.600"
                  _focusVisible={{ borderColor: 'teal.400', boxShadow: '0 0 0 1px var(--chakra-colors-teal-400)' }}
                >
                  {COUNTRY_SELECT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.connectionCountryCode}</FormErrorMessage>
              </FormControl>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" color="whiteAlpha.700" {...streamGhostOnDarkHover} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="teal" onClick={handleSave} isLoading={submitting}>
            Save changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
